// 03d — the form. Five cards. The private-budget card is deliberately, visibly
// different (card-alt fill + the only dashed border in the design) — that
// contrast is the feature that makes the honesty credible (README).
import type { CSSProperties, ReactNode } from 'react';
import { Icon } from '../../lib/icons';
import type { PlanWindow, ReunionForm, TravelMode, WeekOption } from '../../types';
import { MONO } from '../primitives';

const CARD: CSSProperties = {
  background: 'var(--deeper)',
  border: '1px solid rgba(127,227,232,0.24)',
  borderRadius: 16,
  padding: '22px 20px',
};

const CARD_TITLE: CSSProperties = { fontSize: 22, fontWeight: 700, letterSpacing: '-0.02em' };
const MONO_LABEL: CSSProperties = {
  display: 'block',
  fontFamily: MONO,
  fontSize: 12,
  letterSpacing: '0.1em',
  textTransform: 'uppercase',
  color: 'var(--muted)',
  marginBottom: 6,
};
const HELP: CSSProperties = { fontSize: 17, color: 'var(--muted)' };

export function ResponseForm({
  householdLabel,
  form,
  setForm,
  weeks,
  window = null,
  travelModes,
  onSubmit,
  busy,
  banner,
  submitLabel = 'send it to me',
}: {
  householdLabel: string;
  form: ReunionForm;
  setForm: (p: Partial<ReunionForm>) => void;
  weeks: WeekOption[];
  /** Once the date window is decided the week picker gives way to a notice. */
  window?: PlanWindow | null;
  travelModes: TravelMode[];
  onSubmit: () => void;
  busy: boolean;
  /** Override the default "signed in as …" banner (used for admin on-behalf). */
  banner?: ReactNode;
  submitLabel?: string;
}) {
  const toggleWeek = (id: string) =>
    setForm({
      weeks: form.weeks.includes(id) ? form.weeks.filter((x) => x !== id) : [...form.weeks, id],
    });

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
      <div
        style={{
          background: 'rgba(127,227,232,0.1)',
          border: '1px solid rgba(127,227,232,0.35)',
          borderRadius: 12,
          padding: '14px 16px',
          fontSize: 17,
          lineHeight: 1.5,
        }}
      >
        {banner ?? (
          <>
            signed in as <strong>{householdLabel}</strong> — answer for everyone under your roof.
          </>
        )}
      </div>

      {/* weeks — or, once the window is decided, the notice that it is */}
      {window ? (
        <div className="on-card" style={{ ...CARD, borderColor: 'rgba(255,59,31,0.55)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <Icon name="calendar" size={28} color="var(--orange)" style={{ flex: 'none' }} />
            <div style={CARD_TITLE}>the dates are set</div>
          </div>
          <div style={{ fontSize: 'clamp(24px,6vw,28px)', fontWeight: 800, letterSpacing: '-0.03em', color: 'var(--cyan)', margin: '14px 0 8px' }}>
            {window.label}
          </div>
          <div style={{ ...HELP, lineHeight: 1.5, textWrap: 'pretty' }}>
            that's the window. the exact three or four nights come with the place. if none of it works
            for you, say so under "anything else".
          </div>
        </div>
      ) : (
        <div className="on-card" style={CARD}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <Icon name="calendar" size={28} color="var(--cyan)" style={{ flex: 'none' }} />
            <div style={CARD_TITLE}>which of these are already ruled out?</div>
          </div>
          <div style={{ ...HELP, margin: '6px 0 16px' }}>tick anything that doesn't work for you.</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {weeks.map((week) => (
              <label key={week.id} className="wh-option on-card" htmlFor={`wk-${week.id}`}>
                <input
                  id={`wk-${week.id}`}
                  type="checkbox"
                  checked={form.weeks.includes(week.id)}
                  onChange={() => toggleWeek(week.id)}
                />
                <span style={{ fontSize: 20, fontWeight: 600 }}>{week.label}</span>
                {week.note && (
                  <span
                    style={{
                      fontFamily: MONO,
                      fontSize: 12,
                      letterSpacing: '0.1em',
                      textTransform: 'uppercase',
                      color: 'var(--orange)',
                      marginLeft: 'auto',
                    }}
                  >
                    {week.note}
                  </span>
                )}
              </label>
            ))}
          </div>
        </div>
      )}

      {/* headcount */}
      <div className="on-card" style={CARD}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <Icon name="users" size={28} color="var(--cyan)" style={{ flex: 'none' }} />
          <div style={CARD_TITLE}>how many of you, and how old are the kids?</div>
        </div>
        <div style={{ display: 'flex', gap: 12, marginTop: 16 }}>
          <div style={{ flex: 1 }}>
            <label htmlFor="wh-adults" style={MONO_LABEL}>
              adults
            </label>
            <input
              id="wh-adults"
              className="wh-input"
              type="number"
              inputMode="numeric"
              min={0}
              max={20}
              value={form.adults}
              onChange={(e) => setForm({ adults: e.target.value })}
              style={{ minHeight: 60, padding: '12px 14px', fontFamily: MONO, fontSize: 24 }}
            />
          </div>
          <div style={{ flex: 1 }}>
            <label htmlFor="wh-kids" style={MONO_LABEL}>
              kids
            </label>
            <input
              id="wh-kids"
              className="wh-input"
              type="number"
              inputMode="numeric"
              min={0}
              max={20}
              value={form.kids}
              onChange={(e) => setForm({ kids: e.target.value })}
              style={{ minHeight: 60, padding: '12px 14px', fontFamily: MONO, fontSize: 24 }}
            />
          </div>
        </div>
        <label htmlFor="wh-ages" style={{ ...MONO_LABEL, margin: '16px 0 6px' }}>
          kids' ages by summer 2027
        </label>
        <input
          id="wh-ages"
          className="wh-input"
          type="text"
          placeholder="4 and 7, roughly"
          value={form.ages}
          onChange={(e) => setForm({ ages: e.target.value })}
          style={{ minHeight: 60, padding: '12px 14px', fontSize: 20 }}
        />
      </div>

      {/* travel */}
      <div className="on-card" style={CARD}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <Icon name="car" size={28} color="var(--cyan)" style={{ flex: 'none' }} />
          <div style={CARD_TITLE}>would you drive or fly?</div>
        </div>
        <div style={{ ...HELP, margin: '6px 0 16px' }}>this sets how far the place can be.</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {travelModes.map((mode) => (
            <label key={mode.id} className="wh-option on-card" htmlFor={`tm-${mode.id}`}>
              <input
                id={`tm-${mode.id}`}
                type="radio"
                name="wh-travel"
                checked={form.travel === mode.id}
                onChange={() => setForm({ travel: mode.id })}
              />
              <Icon name={mode.icon} size={28} color="var(--cyan)" style={{ flex: 'none' }} />
              <span style={{ fontSize: 20, fontWeight: 600 }}>{mode.label}</span>
            </label>
          ))}
        </div>
      </div>

      {/* private budget */}
      <div
        className="on-card-alt"
        style={{
          background: 'var(--card-alt)',
          border: '2px dashed rgba(127,227,232,0.55)',
          borderRadius: 16,
          padding: '22px 20px',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            fontFamily: MONO,
            fontSize: 12,
            letterSpacing: '0.14em',
            textTransform: 'uppercase',
            color: 'var(--cyan)',
          }}
        >
          <Icon name="lock" size={16} />
          <span>only i see this</span>
        </div>
        <label htmlFor="wh-budget" style={{ ...CARD_TITLE, display: 'block', marginTop: 12 }}>
          what's a comfortable nightly $ budget for lodging?
        </label>
        <div
          style={{
            fontSize: 17,
            color: 'var(--cyan-soft)',
            lineHeight: 1.5,
            margin: '6px 0 14px',
            textWrap: 'pretty',
          }}
        >
          nobody else in the family sees this. i pick for the low end, so i have to know it. a range is
          fine.
        </div>
        <textarea
          id="wh-budget"
          className="wh-input"
          rows={3}
          placeholder="a nightly dollar amount for a bed, a range is fine"
          value={form.budget}
          onChange={(e) => setForm({ budget: e.target.value })}
          style={{ padding: '14px 16px', fontSize: 19, lineHeight: 1.5, resize: 'vertical' }}
        />
      </div>

      {/* anything else */}
      <div className="on-card" style={CARD}>
        <label
          htmlFor="wh-notes"
          style={{ display: 'flex', alignItems: 'center', gap: 12, ...CARD_TITLE }}
        >
          <Icon name="help" size={28} color="var(--cyan)" style={{ flex: 'none' }} />
          anything else?
        </label>
        <div style={{ ...HELP, margin: '6px 0 14px' }}>a bad hip, a dog, a place to suggest.</div>
        <textarea
          id="wh-notes"
          className="wh-input"
          rows={3}
          value={form.notes}
          onChange={(e) => setForm({ notes: e.target.value })}
          style={{ padding: '14px 16px', fontSize: 19, lineHeight: 1.5, resize: 'vertical' }}
        />
      </div>

      <button
        type="button"
        className="wh-cta on-deep"
        onClick={onSubmit}
        disabled={busy}
        style={{ width: '100%', minHeight: 66, fontSize: 22, fontWeight: 700 }}
      >
        {submitLabel}
      </button>
    </div>
  );
}
